import { useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Printer, Download, Loader2 } from 'lucide-react'
import { useReport } from '@/services/admin/reports.service'
import type { ReportType, ReportItem } from '@/services/admin/reports.service'
import { downloadReportCsv } from '@/lib/reportCsv'
import { REPORT_BY_TYPE } from '@/components/super-admin/reports/reports-meta'
import { Button } from '@/components/ui/button'

const VALID_TYPES = Object.keys(REPORT_BY_TYPE) as ReportType[]

function formatBrDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR')
}

/** Lista de cursos/masterclasses dentro de uma célula. */
function ItemCell({ items, render }: { items: ReportItem[]; render: (i: ReportItem) => string }) {
  if (items.length === 0) return <span className="text-gray-400">—</span>
  return (
    <ul className="space-y-1">
      {items.map((i) => (
        <li key={i.courseId} className="leading-tight">
          {render(i)}
        </li>
      ))}
    </ul>
  )
}

export default function ReportView() {
  const { type } = useParams<{ type: string }>()
  const [searchParams] = useSearchParams()

  const isValid = !!type && (VALID_TYPES as string[]).includes(type)
  const meta = isValid ? REPORT_BY_TYPE[type as ReportType] : undefined

  const filters = useMemo(() => {
    const companies = searchParams.get('companies')
    const allTime = searchParams.get('allTime') === '1'
    return {
      companyIds: companies ? companies.split(',').filter(Boolean) : undefined,
      from: allTime ? undefined : (searchParams.get('from') ?? undefined),
      to: allTime ? undefined : (searchParams.get('to') ?? undefined),
      allTime,
    }
  }, [searchParams])

  const { data: report, isLoading, isError } = useReport(type as ReportType, filters, isValid)

  if (!isValid || !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-black">
        <p className="text-red-600">Tipo de relatório inválido.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-black">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Gerando relatório...
      </div>
    )
  }

  if (isError || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-black">
        <p className="text-red-600">
          Erro ao carregar o relatório. Verifique sua sessão e tente novamente.
        </p>
      </div>
    )
  }

  const companyNames = report.companies.map((c) => c.name).join(', ') || 'Todas as empresas'

  const periodLabel = !report.usesPeriod
    ? 'Situação atual (não usa data)'
    : report.period.from || report.period.to
      ? `${formatBrDate(report.period.from)} a ${formatBrDate(report.period.to)}`
      : 'Todo o período'

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Barra de ações — escondida na impressão */}
      <div className="print:hidden sticky top-0 z-10 flex items-center justify-end gap-2 border-b bg-white px-6 py-3">
        <Button variant="outline" size="sm" className="gap-2" onClick={() => window.print()}>
          <Printer className="w-4 h-4" /> Imprimir / Salvar PDF
        </Button>
        <Button size="sm" className="gap-2" onClick={() => downloadReportCsv(report, meta)}>
          <Download className="w-4 h-4" /> Exportar CSV
        </Button>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Cabeçalho */}
        <header className="mb-6 border-b pb-4">
          <h1 className="text-2xl font-bold">Relatório — {meta.title}</h1>
          <div className="mt-2 grid grid-cols-1 gap-1 text-sm text-gray-700 sm:grid-cols-2">
            <p>
              <span className="font-semibold">Empresas:</span> {companyNames}
            </p>
            <p>
              <span className="font-semibold">Período:</span> {periodLabel}
            </p>
            <p>
              <span className="font-semibold">Gerado em:</span> {formatBrDate(report.generatedAt)}
            </p>
          </div>
        </header>

        {/* Tabela */}
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-gray-300 text-left">
              <th className="py-2 pr-3">Nome</th>
              <th className="py-2 pr-3">E-mail</th>
              <th className="py-2 pr-3">Empresa</th>
              {meta.kind === 'registered' && (
                <th className="py-2 pr-3 whitespace-nowrap">Data de cadastro</th>
              )}
              {meta.kind === 'inProgress' && (
                <>
                  <th className="py-2 pr-3">{meta.contentLabel}</th>
                  <th className="py-2 pr-3 whitespace-nowrap">% concluído</th>
                  <th className="py-2 whitespace-nowrap">Última atividade</th>
                </>
              )}
              {meta.kind === 'completed' && (
                <>
                  <th className="py-2 pr-3">{meta.contentLabel}</th>
                  <th className="py-2 whitespace-nowrap">Data de conclusão</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => (
              <tr
                key={row.userId}
                className="border-b border-gray-200 align-top break-inside-avoid"
              >
                <td className="py-2 pr-3 font-medium">{row.name}</td>
                <td className="py-2 pr-3 text-gray-700">{row.email}</td>
                <td className="py-2 pr-3">{row.companyName ?? '—'}</td>
                {meta.kind === 'registered' && (
                  <td className="py-2 pr-3 whitespace-nowrap">{formatBrDate(row.registeredAt)}</td>
                )}
                {meta.kind === 'inProgress' && (
                  <>
                    <td className="py-2 pr-3">
                      <ItemCell items={row.items} render={(i) => i.title} />
                    </td>
                    <td className="py-2 pr-3">
                      <ItemCell items={row.items} render={(i) => `${i.progressPercent ?? 0}%`} />
                    </td>
                    <td className="py-2">
                      <ItemCell items={row.items} render={(i) => formatBrDate(i.lastActivityAt)} />
                    </td>
                  </>
                )}
                {meta.kind === 'completed' && (
                  <>
                    <td className="py-2 pr-3">
                      <ItemCell items={row.items} render={(i) => i.title} />
                    </td>
                    <td className="py-2">
                      <ItemCell items={row.items} render={(i) => formatBrDate(i.completedAt)} />
                    </td>
                  </>
                )}
              </tr>
            ))}
            {report.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-gray-500">
                  Nenhum resultado para os filtros selecionados.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Rodapé — X de Y cadastradas */}
        <footer className="mt-4 border-t pt-3 text-sm text-gray-600">
          Exibindo <strong>{report.rows.length}</strong> de{' '}
          <strong>{report.totalRegistered}</strong> pessoas cadastradas
          {report.companies.length > 0 ? ' nas empresas selecionadas' : ''}.
        </footer>
      </div>
    </div>
  )
}
